import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMoon, FiSun, FiCheck, FiLoader } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import UnauthenticatedMessage from '../components/UnauthenticatedMessage';
import { useTitle } from '../contexts/TitleContext';
import settingsService, { 
  SettingsOptions, 
  UserSettings, 
  AgentOption,
  NarrativeOption,
  ModelTierOption 
} from '../services/settingsService';
import { Button, cn, IconButton } from '../design-system';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { userInfo, loading: authLoading } = useAuth();
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<SettingsOptions | null>(null);
  const [settings, setSettings] = useState<UserSettings>({
    preferred_agent: 'general',
    narrative_style: 'friendly',
    model_tier: 'balanced'
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    setTitle('تنظیمات');
  }, [setTitle]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [optionsData, settingsData] = await Promise.all([
        settingsService.getSettingsOptions(),
        settingsService.getUserSettings()
      ]);
      setOptions(optionsData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await settingsService.saveUserSettings(settings);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">در حال بررسی احراز هویت...</div>
      </div>
    );
  }

  // Show unauthenticated message if user is not logged in
  if (!userInfo?.is_authenticated) {
    return <UnauthenticatedMessage />;
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <FiLoader className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  const optionClassName = (selected: boolean, centered = false) => cn(
    'block cursor-pointer rounded-ds-10 border p-3 transition-colors hover:bg-ds-subtle',
    centered && 'text-center',
    selected ? 'border-ds-primary bg-ds-primary-soft hover:bg-ds-primary-soft' : 'border-ds-200 bg-ds-white',
  );

  return (
    <div className="ds-scrollbar h-full overflow-y-auto bg-ds-canvas px-3 pb-20 pt-5 app-sm:px-8 app-sm:py-8">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <IconButton onClick={() => navigate('/')} aria-label="بازگشت به صفحه اصلی">
              <FiArrowLeft className="rtl-flip" size={19} />
            </IconButton>
            <div>
              <h1 className="text-xl font-black text-ds-ink app-sm:text-2xl">تنظیمات</h1>
              <p className="mt-1 text-[11px] text-ds-500">گزینه‌های دستیار رو مدیریت کن.</p>
            </div>
          </div>
          {hasChanges && (
            <Button
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? (
                <FiLoader className="animate-spin" size={16} />
              ) : (
                <FiCheck size={16} />
              )}
              تغییرات رو ذخیره کن
            </Button>
          )}
        </div>

        <div className="space-y-3">
          <section className="rounded-ds-16 border border-ds-100 bg-ds-white p-[18px]">
            <h2 className="text-[15px] font-bold text-ds-ink">پوسته</h2>
            <p className="mb-3.5 mt-1 text-[11px] leading-5 text-ds-500">
              حالت روشن یا تاریک رو انتخاب کن
            </p>
            <div className="grid grid-cols-1 gap-2.5 app-sm:grid-cols-2">
              <label
                className={optionClassName(theme === 'light', true)}
              >
                <input
                  type="radio"
                  name="theme"
                  value="light"
                  checked={theme === 'light'}
                  onChange={toggleTheme}
                  className="sr-only"
                />
                <div className="flex flex-col items-center gap-2">
                  <FiSun size={20} className="text-ds-warning" />
                  <div className="text-xs font-bold text-ds-ink">روشن</div>
                  <small className="text-[10px] text-ds-500">پس‌زمینه روشن باسلام</small>
                </div>
              </label>
              <label
                className={optionClassName(theme === 'dark', true)}
              >
                <input
                  type="radio"
                  name="theme"
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                  className="sr-only"
                />
                <div className="flex flex-col items-center gap-2">
                  <FiMoon size={20} className="text-ds-700" />
                  <div className="text-xs font-bold text-ds-ink">تاریک</div>
                  <small className="text-[10px] text-ds-500">کنتراست مناسب برای محیط کم‌نور</small>
                </div>
              </label>
            </div>
          </section>

          <section className="rounded-ds-16 border border-ds-100 bg-ds-white p-[18px]">
            <h2 className="text-[15px] font-bold text-ds-ink">دستیارت رو انتخاب کن</h2>
            <p className="mb-3.5 mt-1 text-[11px] leading-5 text-ds-500">
              دستیار مورد نظرت رو برای گفت‌وگو انتخاب کن
            </p>
            <div className="grid gap-2.5 app-sm:grid-cols-2">
              {options?.agents.map((agent: AgentOption) => (
                <label
                  key={agent.id}
                  className={optionClassName(settings.preferred_agent === agent.id)}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="agent"
                      value={agent.id}
                      checked={settings.preferred_agent === agent.id}
                      onChange={(e) => updateSetting('preferred_agent', e.target.value)}
                      className="sr-only"
                    />
                    <div className="text-xl">{agent.avatar}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-bold text-ds-ink">{agent.name}</div>
                      <div className="mt-0.5 truncate text-[10px] text-ds-500">{agent.description}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-ds-16 border border-ds-100 bg-ds-white p-[18px]">
            <h2 className="text-[15px] font-bold text-ds-ink">سبک گفت‌وگو</h2>
            <p className="mb-3.5 mt-1 text-[11px] leading-5 text-ds-500">
              نحوه صحبت دستیار رو انتخاب کن
            </p>
            <div className="grid grid-cols-1 gap-2.5 app-sm:grid-cols-2">
              {options?.narrative_styles.map((style: NarrativeOption) => (
                <label
                  key={style.value}
                  className={optionClassName(settings.narrative_style === style.value, true)}
                >
                  <input
                    type="radio"
                    name="narrative"
                    value={style.value}
                    checked={settings.narrative_style === style.value}
                    onChange={(e) => updateSetting('narrative_style', e.target.value as 'formal' | 'friendly')}
                    className="sr-only"
                  />
                  <div className="text-xs font-bold text-ds-ink">{style.label}</div>
                  <div className="mt-1 text-[10px] text-ds-500">{style.description}</div>
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-ds-16 border border-ds-100 bg-ds-white p-[18px]">
            <h2 className="text-[15px] font-bold text-ds-ink">کیفیت مدل</h2>
            <p className="mb-3.5 mt-1 text-[11px] leading-5 text-ds-500">
              تعادل بین سرعت و کیفیت پاسخ‌ها رو انتخاب کن
            </p>
            <div className="grid grid-cols-1 gap-2.5 app-sm:grid-cols-3">
              {options?.model_tiers.map((tier: ModelTierOption) => (
                <label
                  key={tier.value}
                  className={optionClassName(settings.model_tier === tier.value, true)}
                >
                  <input
                    type="radio"
                    name="model"
                    value={tier.value}
                    checked={settings.model_tier === tier.value}
                    onChange={(e) => updateSetting('model_tier', e.target.value as 'cheap' | 'balanced' | 'expensive')}
                    className="sr-only"
                  />
                  <div className="text-xs font-bold text-ds-ink">{tier.label}</div>
                  <div className="mt-1 text-[10px] text-ds-500">{tier.description}</div>
                </label>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;
